import * as React from "react";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { Input, useMediaQuery } from "@material-ui/core";
import {
  DataProviderContext,
  Loading,
  useGetIdentity,
  useNotify,
  useDataProvider,
} from "react-admin";
import { Form, Field } from "react-final-form";
import { DOCTOR_SALE_SHARE } from "../constants";
import { Receipt, People, MonetizationOn, Save } from "@material-ui/icons";
import { ThemeProvider } from "@material-ui/styles";
import { createTheme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { CircularProgress as MuiLoading } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import "./Dashboard.scss";
import { theme } from "../theme/theme";
import labelsTranslation from "../translations/tranlation";
import {
  gql,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { CREDENTIALS_LOCAL_STORAGE_ITEM } from "../constants";

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem(CREDENTIALS_LOCAL_STORAGE_ITEM);
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token,
    },
  };
});

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_SERVER_ADDRESS_3015}/graphql`,
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

const GET_AUTH_USER = gql`
  query getAuthUser($where: AuthUserWhereUniqueInput!) {
    authUser(where: $where) {
      id
      firstName
      lastName
      roles
    }
  }
`;

const CreatePatientForm = ({ setIsFormSubmitting, isFormSubmitting }) => {
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { loaded, identity } = useGetIdentity();
  const [userInfo, setUserInfo] = React.useState(null);

  const setUserRole = (roles) => {
    if (roles.length === 1) {
      if (roles.includes("doctor_sale.doctor")) {
        return "doctor_sale.doctor";
      } else if (roles.includes("sport_coach.coach")) {
        return "sport_coach.coach";
      }
    } else {
      return "test";
    }
  };
  const getUserInfo = () => {
    identity &&
      client
        .query({
          query: GET_AUTH_USER,
          variables: {
            where: {
              id: identity.id,
            },
          },
        })
        .then((res) => {
          setUserInfo({
            ...userInfo,
            firstName: res.data.authUser.firstName,
            lastName: res.data.authUser.lastName,
            role: setUserRole(res.data.authUser.roles["doctor_sale"]),
          });
        })
        .catch((e) => {
          // console.error(e);
        });
  };
  React.useEffect(() => {
    loaded && getUserInfo();
  }, [loaded]);
  const IranMobileRegex = /^((0)9\d{9})$/;

  const required = (value, fieldLabel) =>
    value ? undefined : "لطفا " + fieldLabel + " را وارد نمایید";
  const regexMobile = (value) =>
    IranMobileRegex.test(value)
      ? undefined
      : "لطفا شماره موبایل رو صحیح وارد نمایید";
  const composeValidators =
    (...validators) =>
    (value) =>
      validators.reduce(
        (error, validator) => error || validator(value),
        undefined
      );
  const TextFieldAdapter = ({ input, meta, ...rest }) => {
    return (
      <div style={{ width: "100%" }}>
        <TextField
          {...input}
          {...rest}
          style={{ margin: "0", width: "100%" }}
          onChange={(event, value) => input.onChange(event)}
          error={meta.touched && meta.error}
        />
        <p
          style={{
            color: "red",
            margin: "0",
            padding: "0",
            fontSize: ".75rem",
          }}
        >
          {meta.touched ? meta.error : ""}
        </p>
      </div>
    );
  };
  const onSubmit = (formValues) => {
    setSubmitLoading(true);
    userInfo &&
      dataProvider
        .create("Patient", {
          data: {
            ...formValues,
            archived: false,
            doctorId: identity?.id,
            doctor: { ...userInfo },
          },
        })
        .then((res) => {
          setSubmitLoading(false);
          setIsFormSubmitting(!isFormSubmitting);
          notify(labelsTranslation.userCreate.notifySuccessMsg, "success");
        })
        .catch((e) => {
          setSubmitLoading(false);
          notify(labelsTranslation.userCreate.notifyFailedMsg, "error");
        });
  };
  return (
    <Form
      onSubmit={onSubmit}
      render={({ handleSubmit, form, submitting, valid, values }) => (
        <form
          className="form"
          onSubmit={async (event) => {
            await handleSubmit(event);
            form.restart();
          }}
        >
          <Field
            name="firstName"
            component={TextFieldAdapter}
            validate={(e) => required(e, labelsTranslation.userCreate.fName)}
            variant="outlined"
            label={labelsTranslation.userCreate.fName}
          />
          <Field
            name="lastName"
            component={TextFieldAdapter}
            validate={(e) => required(e, labelsTranslation.userCreate.lName)}
            variant="outlined"
            label={labelsTranslation.userCreate.lName}
          />
          <Field
            component={TextFieldAdapter}
            validate={composeValidators(
              (e) => required(e, labelsTranslation.userCreate.mobile),
              regexMobile
            )}
            variant="outlined"
            label={labelsTranslation.userCreate.mobile}
            name="mobile"
          />
          <Field
            component={TextFieldAdapter}
            validate={(e) => required(e, labelsTranslation.userCreate.comment)}
            rows={5}
            variant="outlined"
            multiline
            label={labelsTranslation.userCreate.comment}
            name="doctorComment"
          />
          {submitLoading ? (
            <CircularProgress style={{ margin: "0 auto" }} />
          ) : (
            <Button
              disabled={submitting || !valid}
              style={{ padding: "8px" }}
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<Save />}
            >
              {labelsTranslation.userCreate.button}
            </Button>
          )}
        </form>
      )}
    />
  );
};

const StatsCards = ({ isFormSubmitting }) => {
  const { loaded, identity } = useGetIdentity();
  const [loading, setLoading] = React.useState(false);
  const [doctorSalesCount, setDoctorSalesCount] = React.useState(0);
  const [patientsCount, setPatientsCount] = React.useState(0);
  const [totalSettlementAmounts, setTotalSettlementAmounts] = React.useState(0);

  const httpLink = createHttpLink({
    uri: `${process.env.REACT_APP_SERVER_ADDRESS_8091}/graphql`,
  });

  const client = new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache(),
  });

  const getDoctorSalesCountByDoctorId = (doctorId) => {
    return new Promise(async (resolve, reject) => {
      const GET_DOCTOR_SALES_COUNT = gql`
        query getDoctorSalesCount($where: DoctorSaleWhereInput) {
          _doctorSalesMeta(where: $where) {
            count
          }
        }
      `;

      try {
        const res = await client.query({
          query: GET_DOCTOR_SALES_COUNT,
          variables: {
            where: {
              doctorId: {
                equals: doctorId,
              },
            },
          },
        });
        resolve(res.data._doctorSalesMeta.count);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getPatientsCountByDoctorId = (doctorId) => {
    return new Promise(async (resolve, reject) => {
      const GET_PATIENTS_COUNT = gql`
        query getPatientsCount($where: PatientWhereInput) {
          _patientsMeta(where: $where) {
            count
          }
        }
      `;

      try {
        const res = await client.query({
          query: GET_PATIENTS_COUNT,
          variables: {
            where: {
              doctorId: {
                equals: doctorId,
              },
            },
          },
        });
        resolve(res.data._patientsMeta.count);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getTotalSettlementAmounts = (doctorId) => {
    return new Promise(async (resolve, reject) => {
      const GET_SETTLEMENTS_BY_DOCTOR_ID = gql`
        query getSettlementByDoctorId($where: SettlementWhereInput) {
          settlements(where: $where) {
            id
            amount
            doctorId
          }
        }
      `;

      try {
        const res = await client.query({
          query: GET_SETTLEMENTS_BY_DOCTOR_ID,
          variables: {
            where: {
              doctorId: {
                equals: doctorId,
              },
            },
          },
        });
        let totalSettlementAmounts = 0;
        totalSettlementAmounts = res.data.settlements.reduce(
          (acc, settlement) => acc + settlement.amount,
          0
        );
        resolve(totalSettlementAmounts);
      } catch (error) {
        reject(error);
      }
    });
  };

  React.useEffect(() => {
    setLoading(true);

    loaded &&
      Promise.all([
        getPatientsCountByDoctorId(identity.id),
        getDoctorSalesCountByDoctorId(identity.id),
        getTotalSettlementAmounts(identity.id),
      ])
        .then(([patientsCount, doctorSalesCount, totalSettlementAmounts]) => {
          setLoading(false);
          setPatientsCount(patientsCount);
          setDoctorSalesCount(doctorSalesCount);
          setTotalSettlementAmounts(totalSettlementAmounts);
        })
        .catch((e) => {
          setPatientsCount(0);
          setDoctorSalesCount(0);
          setTotalSettlementAmounts(0);
          setLoading(false);
        });
  }, [loaded, isFormSubmitting]);

  return (
    <>
      {!loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "1rem",
            textAlign: "center",
          }}
        >
          <Card
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "16px",
              width: "100%",
            }}
          >
            <People
              style={{
                width: "48px",
                height: "48px",
                color: "#247bee",
              }}
            />
            <p
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                margin: "8px 0",
              }}
            >
              {patientsCount || 0}
            </p>
            <p style={{ fontSize: "1rem", margin: "0" }}>
              {labelsTranslation.dashboard.totalUsers}
            </p>
          </Card>
          <Card
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",

              padding: "16px",
              width: "100%",
            }}
          >
            <Receipt
              style={{
                width: "48px",
                height: "48px",
                color: "#247bee",
              }}
            />
            <p
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                margin: "8px 0",
              }}
            >
              {doctorSalesCount || 0}
            </p>
            <p style={{ fontSize: "1rem", margin: "0" }}>تعداد کل خرید رژیم</p>
          </Card>
          <Card
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",

              padding: "16px",
              width: "100%",
            }}
          >
            <Receipt
              style={{
                width: "48px",
                height: "48px",
                color: "#247bee",
              }}
            />
            <p
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                margin: "8px 0",
              }}
            >
              {totalSettlementAmounts || 0} (ریال)
            </p>
            <p style={{ fontSize: "1rem", margin: "0" }}>
              مجموع مبلغ واریز شده
            </p>
          </Card>
          <Card
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "16px",
              width: "100%",
            }}
          >
            <MonetizationOn
              style={{
                width: "48px",
                height: "48px",
                color: "#247bee",
              }}
            />
            <p
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                margin: "8px 0",
              }}
            >
              {doctorSalesCount ? doctorSalesCount * DOCTOR_SALE_SHARE : 0} (ریال)
            </p>
            <p style={{ fontSize: "1rem", margin: "0" }}>مجموع سهم شما</p>
          </Card>
          <Card
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "16px",
              width: "100%",
            }}
          >
            <MonetizationOn
              style={{
                width: "48px",
                height: "48px",
                color: "#247bee",
              }}
            />
            <p
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                margin: "8px 0",
              }}
            >
              {typeof doctorSalesCount !== "undefined" &&
              typeof totalSettlementAmounts !== "undefined"
                ? doctorSalesCount * DOCTOR_SALE_SHARE - totalSettlementAmounts
                : 0} (ریال)
            </p>
            <p style={{ fontSize: "1rem", margin: "0" }}>مجموع طلب شما</p>
          </Card>
        </div>
      ) : (
        <MuiLoading style={{ marginRight: "42vw" }} />
      )}
    </>
  );
};

const Dashboard = () => {
  const MQBiggerMD = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [isFormSubmitting, setIsFormSubmitting] = React.useState(false);
  return (
    <div className="dashboard">
      <ThemeProvider theme={createTheme(theme)}>
        <div>
          <StatsCards isFormSubmitting={isFormSubmitting} />
          <Card style={{ width: MQBiggerMD ? "50%" : "100%" }}>
            <CardContent>
              <p style={{ fontWeight: "bold", margin: "0" }}>
                <span>فرم</span>
                <span>&nbsp;</span>
                <span>{labelsTranslation.userCreate.title}</span>
              </p>
            </CardContent>
            <CreatePatientForm
              setIsFormSubmitting={setIsFormSubmitting}
              isFormSubmitting={isFormSubmitting}
            />
          </Card>
        </div>
      </ThemeProvider>
    </div>
  );
};

export default Dashboard;
